<template>
  <div class="employee-management-page mainfont pa-4 pa-md-6">
    <!-- Snackbar for Notifications -->
    <v-snackbar v-model="showSnackBar" :color="snackColor" top right :timeout="3500">
      <v-layout wrap align-center>
        <v-flex class="text-left font-weight-medium">
          <v-icon left dark v-if="snackColor === 'success'">mdi-check-circle-outline</v-icon>
          <v-icon left dark v-else>mdi-alert-circle-outline</v-icon>
          <span>{{ msg }}</span>
        </v-flex>
        <v-flex shrink class="text-right">
          <v-btn small text dark @click="showSnackBar = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- Page Loader -->
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#15803d"
      spinner="spinner"
    />

    <!-- Header Section -->
    <v-card class="elevation-1 rounded-lg mb-6 pa-5 white">
      <v-layout wrap align-center justify-space-between>
        <v-flex xs12 md8>
          <div class="d-flex align-center">
            <div class="header-icon-box mr-4">
              <v-icon color="#15803d" size="32">mdi-account-group</v-icon>
            </div>
            <div>
              <h1 class="page-title text-h5 font-weight-bold green--text text--darken-4 mb-1">
                EMPLOYEE LIST
              </h1>
              <p class="text-subtitle-2 text--secondary mb-0">
                Manage Section Clerks and Superintendents assigned to your division
                <v-chip v-if="divisionName" small outlined color="success" class="ml-2 font-weight-medium">
                  {{ divisionName }}
                </v-chip>
              </p>
            </div>
          </div>
        </v-flex>

        <v-flex xs12 md4 class="text-md-right mt-4 mt-md-0">
          <v-btn
            color="#15803d"
            dark
            elevation="2"
            class="rounded-lg text-capitalize px-4"
            @click="openAddDialog"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Add {{ activeTab === 0 ? 'Section Clerk' : 'Superintendent' }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Tab Navigation & Content -->
    <v-card class="elevation-1 rounded-lg overflow-hidden white">
      <v-tabs
        v-model="activeTab"
        background-color="#f0fdf4"
        color="#15803d"
        slider-color="#15803d"
        class="custom-tabs"
      >
        <v-tab class="font-weight-bold text-body-2 py-4">
          <v-icon left color="#15803d">mdi-account-edit-outline</v-icon>
          Section Clerks
          <v-chip small color="#15803d" dark class="ml-2 px-2 font-weight-bold">
            {{ clerksList.length }}
          </v-chip>
        </v-tab>
        <v-tab class="font-weight-bold text-body-2 py-4">
          <v-icon left color="#15803d">mdi-account-tie-outline</v-icon>
          Superintendents
          <v-chip small color="#15803d" dark class="ml-2 px-2 font-weight-bold">
            {{ superList.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <!-- Tabs Items -->
      <v-tabs-items v-model="activeTab" class="pa-4 pa-md-6">
        <!-- TAB 1: SECTION CLERKS -->
        <v-tab-item>
          <!-- Filter / Search Toolbar -->
          <v-layout wrap align-center justify-space-between class="mb-4">
            <v-flex xs12 sm6 md4>
              <v-text-field
                v-model="searchClerk"
                prepend-inner-icon="mdi-magnify"
                label="Search clerks..."
                outlined
                dense
                hide-details
                clearable
                class="rounded-lg custom-search"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 md4 class="text-sm-right mt-3 mt-sm-0">
              <v-btn
                outlined
                color="#15803d"
                small
                class="rounded-lg text-capitalize"
                @click="loadClerks"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-flex>
          </v-layout>

          <!-- Clerks Data Table -->
          <v-data-table
            :headers="headers"
            :items="clerksList"
            :search="searchClerk"
            :loading="loadingClerks"
            class="elevation-0 employee-table"
            no-data-text="No Section Clerks found for this division"
            no-results-text="No matching Section Clerks found"
          >
            <!-- S.No Slot -->
            <template v-slot:item.sno="{ index }">
              <span class="font-weight-medium text--secondary">{{ index + 1 }}</span>
            </template>

            <!-- Username / Avatar Slot -->
            <template v-slot:item.username="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar color="#e8f5e9" size="36" class="mr-3">
                  <v-icon color="#15803d" size="20">mdi-account</v-icon>
                </v-avatar>
                <div class="font-weight-bold green--text text--darken-4 text-body-2">
                  {{ item.username }}
                </div>
              </div>
            </template>

            <!-- Status Slot -->
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="item.status === 'Active' ? 'green lighten-5' : 'grey lighten-3'"
                :text-color="item.status === 'Active' ? 'green darken-3' : 'grey darken-3'"
                class="font-weight-medium px-3"
              >
                <v-icon left x-small :color="item.status === 'Active' ? 'green darken-3' : 'grey darken-3'">
                  mdi-circle
                </v-icon>
                {{ item.status || 'Active' }}
              </v-chip>
            </template>

            <!-- Actions Slot -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="teal darken-2"
                      v-bind="attrs"
                      v-on="on"
                      class="mr-1"
                      @click="editEmployee(item)"
                    >
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit Clerk</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="red darken-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmDelete(item, 'Section Clerk')"
                    >
                      <v-icon size="19">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Clerk</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- TAB 2: SUPERINTENDENTS -->
        <v-tab-item>
          <!-- Filter / Search Toolbar -->
          <v-layout wrap align-center justify-space-between class="mb-4">
            <v-flex xs12 sm6 md4>
              <v-text-field
                v-model="searchSuper"
                prepend-inner-icon="mdi-magnify"
                label="Search superintendents..."
                outlined
                dense
                hide-details
                clearable
                class="rounded-lg custom-search"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 md4 class="text-sm-right mt-3 mt-sm-0">
              <v-btn
                outlined
                color="#15803d"
                small
                class="rounded-lg text-capitalize"
                @click="loadSuperintendents"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-flex>
          </v-layout>

          <!-- Superintendents Data Table -->
          <v-data-table
            :headers="headers"
            :items="superList"
            :search="searchSuper"
            :loading="loadingSuper"
            class="elevation-0 employee-table"
            no-data-text="No Superintendents found for this division"
            no-results-text="No matching Superintendents found"
          >
            <!-- S.No Slot -->
            <template v-slot:item.sno="{ index }">
              <span class="font-weight-medium text--secondary">{{ index + 1 }}</span>
            </template>

            <!-- Username / Avatar Slot -->
            <template v-slot:item.username="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar color="#e0f2fe" size="36" class="mr-3">
                  <v-icon color="#0284c7" size="20">mdi-account-tie</v-icon>
                </v-avatar>
                <div class="font-weight-bold green--text text--darken-4 text-body-2">
                  {{ item.username }}
                </div>
              </div>
            </template>

            <!-- Status Slot -->
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="item.status === 'Active' ? 'green lighten-5' : 'grey lighten-3'"
                :text-color="item.status === 'Active' ? 'green darken-3' : 'grey darken-3'"
                class="font-weight-medium px-3"
              >
                <v-icon left x-small :color="item.status === 'Active' ? 'green darken-3' : 'grey darken-3'">
                  mdi-circle
                </v-icon>
                {{ item.status || 'Active' }}
              </v-chip>
            </template>

            <!-- Actions Slot -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="teal darken-2"
                      v-bind="attrs"
                      v-on="on"
                      class="mr-1"
                      @click="editEmployee(item)"
                    >
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit Superintendent</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="red darken-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmDelete(item, 'Superintendent')"
                    >
                      <v-icon size="19">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Superintendent</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ADD / EDIT MODAL DIALOG -->
    <v-dialog v-model="dialogForm" max-width="560px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="pa-5 pb-4 green darken-4 white--text d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">
              {{ isEditMode ? 'mdi-account-edit' : 'mdi-account-plus' }}
            </v-icon>
            <span class="text-h6 font-weight-bold">
              {{ isEditMode ? 'Edit ' + currentRoleName : 'Add New ' + currentRoleName }}
            </span>
          </div>
          <v-btn icon dark small @click="dialogForm = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-5 pa-md-6 pb-2">
          <v-form ref="employeeForm" v-model="isFormValid" lazy-validation>
            <v-layout wrap>
              <!-- Username -->
              <v-flex xs12>
                <v-text-field
                  v-model="formData.username"
                  label="Username *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  :rules="usernameRules"
                  class="rounded-lg mb-2"
                ></v-text-field>
              </v-flex>

              <!-- Office Name -->
              <v-flex xs12>
                <v-text-field
                  v-model="formData.officeName"
                  label="Office Name *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-office-building"
                  :rules="officeRules"
                  class="rounded-lg mb-2"
                ></v-text-field>
              </v-flex>

              <!-- Mobile Number -->
              <v-flex xs12 sm6 pr-sm-2>
                <v-text-field
                  v-model="formData.mobNumber"
                  label="Mobile Number *"
                  outlined
                  dense
                  type="tel"
                  maxlength="10"
                  prepend-inner-icon="mdi-phone"
                  :rules="mobileRules"
                  class="rounded-lg mb-2"
                ></v-text-field>
              </v-flex>

              <!-- Email ID -->
              <v-flex xs12 sm6 pl-sm-2>
                <v-text-field
                  v-model="formData.emailId"
                  label="Email ID *"
                  outlined
                  dense
                  type="email"
                  prepend-inner-icon="mdi-email"
                  :rules="emailRules"
                  class="rounded-lg mb-2"
                ></v-text-field>
              </v-flex>

              <!-- Password (Only on Add) -->
              <v-flex xs12 v-if="!isEditMode">
                <v-text-field
                  v-model="formData.password"
                  label="Password *"
                  outlined
                  dense
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showPassword = !showPassword"
                  prepend-inner-icon="mdi-lock"
                  :rules="passwordRules"
                  hint="At least 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char"
                  persistent-hint
                  class="rounded-lg mb-3"
                ></v-text-field>
              </v-flex>

              <!-- Confirm Password (Only on Add) -->
              <v-flex xs12 v-if="!isEditMode">
                <v-text-field
                  v-model="formData.confirmPassword"
                  label="Confirm Password *"
                  outlined
                  dense
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                  prepend-inner-icon="mdi-lock-check"
                  :rules="confirmPasswordRules"
                  class="rounded-lg mb-2"
                ></v-text-field>
              </v-flex>

              <!-- Status (Only on Edit) -->
              <v-flex xs12 v-if="isEditMode">
                <v-select
                  v-model="formData.status"
                  :items="['Active', 'Inactive']"
                  label="Status *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-toggle-switch"
                  class="rounded-lg mb-2"
                ></v-select>
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-5 pb-5 pt-0 d-flex justify-end">
          <v-btn
            outlined
            color="grey darken-1"
            class="rounded-lg text-capitalize px-4 mr-2"
            @click="dialogForm = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#15803d"
            dark
            class="rounded-lg text-capitalize px-5"
            :loading="savingEmployee"
            @click="saveEmployee"
          >
            <v-icon left small>mdi-content-save</v-icon>
            {{ isEditMode ? 'Update ' + currentRoleName : 'Save ' + currentRoleName }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE CONFIRMATION DIALOG -->
    <v-dialog v-model="dialogDelete" max-width="440px">
      <v-card class="rounded-lg">
        <v-card-title class="pa-5 pb-3 red darken-1 white--text d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          <span class="text-h6 font-weight-bold">Confirm Deletion</span>
        </v-card-title>
        <v-card-text class="pa-5 pt-4">
          <p class="text-body-1 mb-2">
            Are you sure you want to delete
            <strong>{{ employeeToDelete ? employeeToDelete.username : 'this employee' }}</strong>?
          </p>
          <p class="caption text--secondary mb-0">
            This will permanently remove the {{ roleToDelete }} account and revoke all system access.
          </p>
        </v-card-text>
        <v-card-actions class="px-5 pb-5 d-flex justify-end">
          <v-btn
            outlined
            color="grey darken-1"
            class="rounded-lg text-capitalize px-4 mr-2"
            @click="dialogDelete = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="red darken-1"
            dark
            class="rounded-lg text-capitalize px-4"
            :loading="deletingEmployee"
            @click="deleteEmployeeAction"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete Employee
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "EmployeeList",
  data() {
    return {
      activeTab: 0,
      appLoading: false,
      loadingClerks: false,
      loadingSuper: false,
      savingEmployee: false,
      deletingEmployee: false,

      // Search filters
      searchClerk: "",
      searchSuper: "",

      // Employee lists
      clerksList: [],
      superList: [],

      // Table headers
      headers: [
        { text: "S.NO", value: "sno", width: "70px", sortable: false },
        { text: "EMPLOYEE NAME", value: "username" },
        { text: "OFFICE NAME", value: "officeName" },
        { text: "MOBILE NUMBER", value: "mobNumber" },
        { text: "EMAIL ID", value: "emailId" },
        { text: "STATUS", value: "status", width: "120px" },
        { text: "ACTIONS", value: "actions", width: "110px", sortable: false },
      ],

      // Dialogs
      dialogForm: false,
      dialogDelete: false,
      isEditMode: false,
      isFormValid: true,
      showPassword: false,
      showConfirmPassword: false,

      // Selected items
      employeeToDelete: null,
      roleToDelete: "",

      // Form Data
      formData: {
        _id: null,
        username: "",
        officeName: "",
        mobNumber: "",
        emailId: "",
        password: "",
        confirmPassword: "",
        status: "Active",
      },

      // Notifications
      msg: "",
      showSnackBar: false,
      snackColor: "success",
    };
  },

  computed: {
    divisionName() {
      return this.$store.state.dname || "";
    },
    currentRoleName() {
      return this.activeTab === 0 ? "Section Clerk" : "Superintendent";
    },

    // Form validation rules
    usernameRules() {
      return [
        (v) => !!v || "Username is required",
        (v) => (v && v.length >= 3) || "Username must be at least 3 characters",
      ];
    },
    officeRules() {
      return [(v) => !!v || "Office Name is required"];
    },
    mobileRules() {
      return [
        (v) => !!v || "Mobile number is required",
        (v) => /^[0-9]{10}$/.test(v) || "Mobile number must be exactly 10 digits",
      ];
    },
    emailRules() {
      return [
        (v) => !!v || "Email address is required",
        (v) => /.+@.+\..+/.test(v) || "Please enter a valid email address",
      ];
    },
    passwordRules() {
      if (this.isEditMode) return [];
      return [
        (v) => !!v || "Password is required",
        (v) => (v && v.length >= 8) || "Password must be at least 8 characters",
        (v) => /[a-z]/.test(v) || "Password must contain at least one lowercase letter",
        (v) => /[A-Z]/.test(v) || "Password must contain at least one uppercase letter",
        (v) => /[0-9]/.test(v) || "Password must contain at least one digit",
        (v) => /[!@#$%^&*(),.?":{}|<>]/.test(v) || "Password must contain at least one special character",
      ];
    },
    confirmPasswordRules() {
      if (this.isEditMode) return [];
      return [
        (v) => !!v || "Confirm password is required",
        (v) => v === this.formData.password || "Passwords do not match",
      ];
    },
  },

  mounted() {
    // Check if query param specified a tab
    if (this.$route.query.tab === "superintendent" || this.$route.query.tab === "1") {
      this.activeTab = 1;
    }
    this.loadAllEmployees();
  },

  methods: {
    loadAllEmployees() {
      this.loadClerks();
      this.loadSuperintendents();
    },

    // Fetch Clerks
    loadClerks() {
      this.loadingClerks = true;
      axios({
        method: "GET",
        url: "/list/clerk",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.loadingClerks = false;
          if (response.data.status && response.data.data) {
            // Check if returned data is an array or a single object
            if (Array.isArray(response.data.data)) {
              this.clerksList = response.data.data;
            } else {
              this.clerksList = [response.data.data];
            }
          } else {
            this.clerksList = [];
          }
        })
        .catch((err) => {
          this.loadingClerks = false;
          console.error("Error fetching clerks:", err);
          this.clerksList = [];
        });
    },

    // Fetch Superintendents
    loadSuperintendents() {
      this.loadingSuper = true;
      axios({
        method: "GET",
        url: "/list/superintendent",
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.loadingSuper = false;
          if (response.data.status && response.data.data) {
            if (Array.isArray(response.data.data)) {
              this.superList = response.data.data;
            } else {
              this.superList = [response.data.data];
            }
          } else {
            this.superList = [];
          }
        })
        .catch((err) => {
          this.loadingSuper = false;
          console.error("Error fetching superintendents:", err);
          this.superList = [];
        });
    },

    // Open Add Dialog
    openAddDialog() {
      this.isEditMode = false;
      this.showPassword = false;
      this.showConfirmPassword = false;
      this.formData = {
        _id: null,
        username: "",
        officeName: "",
        mobNumber: "",
        emailId: "",
        password: "",
        confirmPassword: "",
        status: "Active",
      };
      if (this.$refs.employeeForm) {
        this.$refs.employeeForm.resetValidation();
      }
      this.dialogForm = true;
    },

    // Open Edit Dialog
    editEmployee(item) {
      this.isEditMode = true;
      this.showPassword = false;
      this.showConfirmPassword = false;
      this.formData = {
        _id: item._id,
        username: item.username || "",
        officeName: item.officeName || "",
        mobNumber: item.mobNumber || "",
        emailId: item.emailId || "",
        password: "",
        confirmPassword: "",
        status: item.status || "Active",
      };
      if (this.$refs.employeeForm) {
        this.$refs.employeeForm.resetValidation();
      }
      this.dialogForm = true;
    },

    // Save or Update Employee
    saveEmployee() {
      if (!this.$refs.employeeForm.validate()) {
        return;
      }

      this.savingEmployee = true;
      const isClerk = this.activeTab === 0;

      if (!this.isEditMode) {
        // Add Employee
        const postUrl = isClerk ? "/add/clerk" : "/add/superintendent";
        axios({
          method: "POST",
          url: postUrl,
          headers: { token: localStorage.getItem("token") },
          data: {
            username: this.formData.username,
            officeName: this.formData.officeName,
            mobNumber: this.formData.mobNumber,
            emailId: this.formData.emailId,
            password: this.formData.password,
            confirmPassword: this.formData.confirmPassword,
          },
        })
          .then((response) => {
            this.savingEmployee = false;
            if (response.data.status) {
              this.dialogForm = false;
              this.notify(response.data.msg || `${this.currentRoleName} added successfully!`, "success");
              if (isClerk) {
                this.loadClerks();
              } else {
                this.loadSuperintendents();
              }
            } else {
              this.notify(response.data.msg || "Failed to add employee.", "error");
            }
          })
          .catch((err) => {
            this.savingEmployee = false;
            console.error("Error adding employee:", err);
            this.notify("Server error while adding employee.", "error");
          });
      } else {
        // Update Employee
        axios({
          method: "PUT",
          url: `/edit/edituser/${this.formData._id}`,
          headers: { token: localStorage.getItem("token") },
          data: {
            username: this.formData.username,
            officeName: this.formData.officeName,
            mobNumber: this.formData.mobNumber,
            emailId: this.formData.emailId,
            status: this.formData.status,
          },
        })
          .then((response) => {
            this.savingEmployee = false;
            if (response.data.status) {
              this.dialogForm = false;
              this.notify(response.data.msg || "Employee updated successfully!", "success");
              if (isClerk) {
                this.loadClerks();
              } else {
                this.loadSuperintendents();
              }
            } else {
              this.notify(response.data.msg || "Failed to update employee.", "error");
            }
          })
          .catch((err) => {
            this.savingEmployee = false;
            console.error("Error updating employee:", err);
            this.notify("Server error while updating employee.", "error");
          });
      }
    },

    // Confirm Delete
    confirmDelete(item, roleName) {
      this.employeeToDelete = item;
      this.roleToDelete = roleName;
      this.dialogDelete = true;
    },

    // Perform Delete
    deleteEmployeeAction() {
      if (!this.employeeToDelete) return;
      this.deletingEmployee = true;

      axios({
        method: "DELETE",
        url: `/delete/employee/${this.employeeToDelete._id}`,
        headers: { token: localStorage.getItem("token") },
      })
        .then((response) => {
          this.deletingEmployee = false;
          if (response.data.status) {
            this.dialogDelete = false;
            this.notify(response.data.msg || `${this.roleToDelete} deleted successfully.`, "success");
            this.loadAllEmployees();
          } else {
            this.notify(response.data.msg || "Failed to delete employee.", "error");
          }
        })
        .catch((err) => {
          this.deletingEmployee = false;
          console.error("Error deleting employee:", err);
          this.notify("Server error while deleting employee.", "error");
        });
    },

    notify(message, color = "success") {
      this.msg = message;
      this.snackColor = color;
      this.showSnackBar = true;
    },
  },
};
</script>

<style scoped>
.employee-management-page {
  background-color: #f8fafc;
  min-height: calc(100vh - 64px);
}

.header-icon-box {
  background-color: #e8f5e9;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  letter-spacing: -0.5px;
}

.custom-tabs >>> .v-tab {
  letter-spacing: 0.3px;
  text-transform: none;
}

.custom-tabs >>> .v-tab--active {
  color: #15803d !important;
}

.employee-table >>> th {
  background-color: #f8fafc !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #334155 !important;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0 !important;
}

.employee-table >>> td {
  font-size: 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.employee-table >>> tr:hover {
  background-color: #f0fdf4 !important;
}

.custom-search >>> .v-input__control {
  border-radius: 8px;
}
</style>
